import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { projectApi } from "../../api/projectApi";
import { useAuth } from "../../context/AuthContext";

// ── 클린 디자인 토큰 (라이트 모드 기반) ───────────────────────
const T = {
  bg:        "#f8fafc",
  card:      "#ffffff",
  border:    "#e2e8f0",
  text:      "#0f172a",
  textMuted: "#64748b",
  accent:    "#0ea5e9",
  danger:    "#ef4444",
  radius:    "12px",
};

// ── 마크다운 렌더러 컴포넌트 (클린 스타일) ──────────────────
const mdComponents = {
  h1: ({ children }) => (
    <h1 style={{ fontSize:"2rem", fontWeight:800, margin:"2rem 0 1rem", color:T.text }}>{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 style={{ fontSize:"1.6rem", fontWeight:700, margin:"1.8rem 0 0.8rem", color:T.text, borderBottom:`1.5px solid ${T.border}`, paddingBottom:"0.4rem" }}>{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 style={{ fontSize:"1.3rem", fontWeight:600, margin:"1.5rem 0 0.6rem", color:T.text }}>{children}</h3>
  ),
  p:  ({ children }) => (
    <p style={{ margin:"1rem 0", lineHeight:"1.7", color:"#334155", fontSize:"16px" }}>{children}</p>
  ),
  a:  ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer" style={{ color:T.accent, textDecoration:"none", fontWeight:600 }}>{children}</a>
  ),
  ul: ({ children }) => (
    <ul style={{ margin:"1rem 0", paddingLeft:"1.5rem", color:"#334155" }}>{children}</ul>
  ),
  li: ({ children }) => (
    <li style={{ margin:"0.4rem 0", lineHeight:"1.6" }}>{children}</li>
  ),
  blockquote: ({ children }) => (
    <blockquote style={{ margin:"1.5rem 0", padding:"1rem 1.5rem", borderLeft:`4px solid ${T.accent}`, background:`#f0f9ff`, color:T.textMuted }}>{children}</blockquote>
  ),
  code: ({ inline, children }) =>
    inline ? (
      <code style={{ background:"#f1f5f9", padding:"2px 5px", borderRadius:"4px", fontSize:"0.9em", color:T.accent }}>{children}</code>
    ) : (
      <pre style={{ background:"#1e293b", border:`1px solid ${T.border}`, borderRadius:"8px", padding:"16px", overflowX:"auto", margin:"1.5rem 0" }}>
        <code style={{ fontSize:"0.9em", color:"#f8fafc", lineHeight:"1.5" }}>{children}</code>
      </pre>
    ),
  img: ({ src, alt }) => (
    <div style={{ margin:"2rem 0", textAlign:"center" }}>
      <img src={src} alt={alt} style={{ maxWidth:"100%", borderRadius:"12px", border:`1px solid ${T.border}`, boxShadow:"0 4px 20px rgba(0,0,0,0.05)" }} />
    </div>
  ),
};

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [deleting, setDeleting] = useState(false);

  const viewedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const fetchProject = async () => {
      try {
        setLoading(true);
        if (!viewedRef.current) {
          viewedRef.current = true;
          await projectApi.increaseView(id).catch(() => {});
        }
        const { data } = await projectApi.getOne(id);
        if (!cancelled) setProject(data);
      } catch {
        if (!cancelled) setError("Project loading failed.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProject();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    viewedRef.current = false;
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    setDeleting(true);
    try {
      await projectApi.remove(id);
      navigate("/projects", { replace: true });
    } catch {
      alert("삭제 실패.");
      setDeleting(false);
    }
  };

  if (loading) return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center", color:T.textMuted }}>로딩 중...</div>
  );

  if (error || !project) return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
      <p style={{ color:T.danger }}>{error || "프로젝트를 찾을 수 없습니다."}</p>
      <button onClick={() => navigate("/projects")} style={{ color:T.accent, background:"none", border:"none", cursor:"pointer", marginTop:"16px" }}>← 목록으로 돌아가기</button>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:T.bg, color:T.text, padding:"100px 24px" }}>
      <main style={{ maxWidth:"840px", margin:"0 auto" }}>
        
        {/* 네비게이션 */}
        <button
          onClick={() => navigate("/projects")}
          style={{ background:"none", border:"none", cursor:"pointer", color:T.textMuted, fontSize:"14px", marginBottom:"32px", display:"flex", alignItems:"center", gap:"8px" }}
        >
          ← 목록으로 돌아가기
        </button>

        {/* 상단 메타 */}
        <div style={{ marginBottom:"40px" }}>
          <div style={{ display:"flex", gap:"8px" }}>
            {(Array.isArray(project.category) ? project.category : [project.category || "dev"]).map(c => (
              <span key={c} style={{ fontSize:"11px", fontWeight:800, color:T.accent, textTransform:"uppercase", letterSpacing:"0.1em" }}>
                {c === "dev" ? "개발" : "보안"}
              </span>
            ))}
          </div>
          <h1 style={{ fontSize:"clamp(2rem, 5vw, 3rem)", fontWeight:800, color:T.text, marginTop:"8px", marginBottom:"16px" }}>{project.title}</h1>
          <div style={{ display:"flex", gap:"20px", fontSize:"13px", color:T.textMuted, fontWeight:500, marginBottom:"32px" }}>
            {project.period && <span><strong style={{ color:T.text }}>기간:</strong> {project.period}</span>}
            {project.role && <span><strong style={{ color:T.text }}>담당:</strong> {project.role}</span>}
            <span>👁 {project.views?.toLocaleString()}</span>
          </div>

          {project.thumbnail_url && (
            <div style={{ width:"100%", height:"450px", borderRadius:"24px", overflow:"hidden", marginBottom:"48px", boxShadow:"0 20px 40px rgba(0,0,0,0.1)" }}>
              <img src={project.thumbnail_url} alt={project.title} style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
            </div>
          )}
        </div>

        {/* 프로젝트 컨테이너 카드 */}
        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:"20px", padding:"40px", boxShadow:"0 4px 20px rgba(0,0,0,0.03)" }}>
          
          {/* 링크 버튼 */}
          {(project.github_url || project.demo_url) && (
            <div style={{ display:"flex", gap:"10px", marginBottom:"40px" }}>
              {project.github_url && (
                <a href={project.github_url} target="_blank" rel="noreferrer" style={{ padding:"10px 20px", borderRadius:"99px", background:T.text, color:"#fff", fontWeight:700, fontSize:"14px", textDecoration:"none" }}>GitHub</a>
              )}
              {project.demo_url && (
                <a href={project.demo_url} target="_blank" rel="noreferrer" style={{ padding:"10px 20px", borderRadius:"99px", background:"#fff", border:`1.5px solid ${T.border}`, color:T.text, fontWeight:700, fontSize:"14px", textDecoration:"none" }}>Live Demo</a>
              )}
            </div>
          )}

          {/* 본문 요약 */}
          {project.summary && (
            <p style={{ fontSize:"1.2rem", fontWeight:500, color:T.text, lineHeight:"1.6", marginBottom:"40px", borderLeft:`4px solid ${T.accent}`, paddingLeft:"20px" }}>
              {project.summary}
            </p>
          )}

          {/* 상세 내용 */}
          <section>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {project.content}
            </ReactMarkdown>
          </section>

          {/* 관리자 도구 */}
          {isAdmin && (
            <div style={{ marginTop:"60px", paddingTop:"30px", borderTop:`1px solid ${T.border}`, display:"flex", justifyContent:"flex-end", gap:"8px" }}>
              <button onClick={() => navigate(`/projects/${id}/edit`)} style={{ padding:"8px 20px", borderRadius:"8px", border:`1px solid ${T.border}`, background:"#fff", cursor:"pointer", fontSize:"13px", fontWeight:600 }}>수정</button>
              <button onClick={handleDelete} disabled={deleting} style={{ padding:"8px 20px", borderRadius:"8px", border:"none", background:T.danger, color:"#fff", cursor:deleting?"not-allowed":"pointer", fontSize:"13px", fontWeight:600 }}>{deleting ? "삭제 중..." : "삭제"}</button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProjectDetailPage;
